import {
  constant,
  isBoolean,
  isPlainObject,
  isString,
  snakeCase,
  withProperties
} from "../utils";

/**
 * Attribute bit-mask for supported currency.
 * Set for currencies that are currently supported for payments.
 * Turns on only the bit at position 1 (from the right).
 * ----------------
 *   1 => (0 0 1)
 * ----------------
 */
const FLAG_SUPPORTED = 0x1;

/**
 * Attribute bit-mask for DCC target currency.
 * Set for currencies that are DCC (Dynamic Currency Conversion) targets.
 * Turns on only the bit at position 2 (from the right).
 * ----------------
 *   2 => (0 1 0)
 * ----------------
 */
const FLAG_DCC_TARGET = 0x2;

/**
 * Attribute bit-mask for rounded amount currency.
 * Set for currencies that require their amounts to always be rounded.
 * Turns on only the bit at position 3 (from the right).
 * ----------------
 *   4 => (1 0 0)
 * ----------------
 */
const FLAG_ROUNDED_AMOUNT = 0x4;

const FLAG_ALL = FLAG_SUPPORTED | FLAG_DCC_TARGET | FLAG_ROUNDED_AMOUNT;

/**
 * Map of known currencies to their corresponding attributes flags.
 *
 * The attributes flag is a 3-bit integer with each bit indicating
 * the following (starting from the rightmost bit):
 *
 *   - (00X) if currency is supported
 *   - (0X0) if currency is a DCC target
 *   - (X00) if currency requires rounded amounts
 *
 * The attributes flag can be composed from bit-masks for the
 * corresponding bits. For example, the attributes flag for a currency
 * that is supported and also requires rounded amounts can be composed
 * as follows:
 * -----------------------------------------
 *   FLAG_SUPPORTED | FLAG_ROUNDED_AMOUNT
 * -----------------------------------------
 *
 * Using the above example, the resulting attributes flag for the
 * currency will be:
 * -----------------------------------------
 *   (0 0 1) | (1 0 0)  =>  (1 0 1)  =>  5
 * -----------------------------------------
 */
const CURRENCIES = {
  AED: FLAG_SUPPORTED,
  ALL: FLAG_SUPPORTED,
  ARS: FLAG_SUPPORTED,
  AUD: FLAG_SUPPORTED,
  BGN: FLAG_SUPPORTED,
  BHD: FLAG_SUPPORTED,
  BIF: FLAG_SUPPORTED | FLAG_ROUNDED_AMOUNT,
  BND: FLAG_SUPPORTED,
  BRL: FLAG_SUPPORTED,
  BWP: FLAG_SUPPORTED,
  CAD: FLAG_SUPPORTED,
  CDF: FLAG_SUPPORTED,
  CFA: FLAG_SUPPORTED,
  CHF: FLAG_SUPPORTED,
  CLP: FLAG_ROUNDED_AMOUNT | FLAG_SUPPORTED,
  CNY: FLAG_SUPPORTED,
  COP: FLAG_SUPPORTED,
  CRC: FLAG_SUPPORTED,
  CVE: FLAG_SUPPORTED,
  CZK: FLAG_SUPPORTED,
  DJF: FLAG_ROUNDED_AMOUNT,
  DKK: FLAG_SUPPORTED,
  DOP: FLAG_SUPPORTED,
  DZD: FLAG_SUPPORTED,
  EGP: FLAG_SUPPORTED,
  ETB: FLAG_SUPPORTED,
  EUR: FLAG_SUPPORTED | FLAG_DCC_TARGET,
  GBP: FLAG_SUPPORTED | FLAG_DCC_TARGET,
  GHS: FLAG_SUPPORTED,
  GMD: FLAG_SUPPORTED,
  GNF: FLAG_SUPPORTED | FLAG_ROUNDED_AMOUNT,
  GTQ: FLAG_SUPPORTED,
  HKD: FLAG_SUPPORTED,
  HNL: FLAG_SUPPORTED,
  HUF: FLAG_SUPPORTED,
  IDR: FLAG_SUPPORTED,
  ILS: FLAG_SUPPORTED,
  INR: FLAG_SUPPORTED,
  IQD: FLAG_SUPPORTED,
  ISK: FLAG_ROUNDED_AMOUNT | FLAG_SUPPORTED,
  JOD: FLAG_SUPPORTED,
  JPY: FLAG_SUPPORTED | FLAG_ROUNDED_AMOUNT,
  KES: FLAG_SUPPORTED,
  KHR: FLAG_SUPPORTED,
  KMF: FLAG_ROUNDED_AMOUNT,
  KRW: FLAG_SUPPORTED | FLAG_ROUNDED_AMOUNT,
  KWD: FLAG_SUPPORTED,
  LBP: FLAG_SUPPORTED,
  LKR: FLAG_SUPPORTED,
  LRD: FLAG_SUPPORTED,
  LYD: FLAG_SUPPORTED,
  MAD: FLAG_SUPPORTED,
  MOP: FLAG_SUPPORTED,
  MUR: FLAG_SUPPORTED,
  MWK: FLAG_SUPPORTED,
  MXN: FLAG_SUPPORTED,
  MYR: FLAG_SUPPORTED,
  MZN: FLAG_SUPPORTED,
  NGN: FLAG_SUPPORTED | FLAG_DCC_TARGET,
  NOK: FLAG_SUPPORTED,
  NZD: FLAG_SUPPORTED,
  OMR: FLAG_SUPPORTED,
  PAB: FLAG_SUPPORTED,
  PEN: FLAG_SUPPORTED,
  PHP: FLAG_SUPPORTED,
  PLN: FLAG_SUPPORTED,
  PYG: FLAG_ROUNDED_AMOUNT | FLAG_SUPPORTED,
  QAR: FLAG_SUPPORTED,
  RUB: FLAG_SUPPORTED,
  RWF: FLAG_SUPPORTED | FLAG_ROUNDED_AMOUNT,
  SAR: FLAG_SUPPORTED,
  SDD: FLAG_SUPPORTED,
  SEK: FLAG_SUPPORTED,
  SGD: FLAG_SUPPORTED,
  SLL: FLAG_SUPPORTED,
  STD: FLAG_SUPPORTED,
  SVC: FLAG_SUPPORTED,
  SYP: FLAG_SUPPORTED,
  THB: FLAG_SUPPORTED,
  TND: FLAG_SUPPORTED | FLAG_ROUNDED_AMOUNT,
  TRY: FLAG_SUPPORTED,
  TWD: FLAG_SUPPORTED,
  TZS: FLAG_SUPPORTED,
  UGX: FLAG_SUPPORTED | FLAG_ROUNDED_AMOUNT,
  USD: FLAG_SUPPORTED | FLAG_DCC_TARGET,
  UYI: FLAG_ROUNDED_AMOUNT,
  VEF: FLAG_SUPPORTED,
  VND: FLAG_ROUNDED_AMOUNT | FLAG_SUPPORTED,
  VUV: FLAG_ROUNDED_AMOUNT,
  XAF: FLAG_SUPPORTED | FLAG_ROUNDED_AMOUNT,
  XOF: FLAG_SUPPORTED | FLAG_ROUNDED_AMOUNT,
  XPF: FLAG_ROUNDED_AMOUNT,
  YER: FLAG_SUPPORTED,
  ZAR: FLAG_SUPPORTED | FLAG_DCC_TARGET,
  ZMK: FLAG_SUPPORTED,
  ZMW: FLAG_SUPPORTED,
  ZWD: FLAG_SUPPORTED
};

/**
 * Default currency-like string that can be used anywhere a currency-like
 * is expected, especially as a fallback whenever a given currency cannot
 * normalized to a currency-like string.
 *
 */
const DEFAULT_NO_CURRENCY = "XXX";

/**
 * Regular expression that can be used to test a string to determine if
 * it can be normalized to a currency-like string.
 */
const REGEX_LIKE_CURRENCY = /^\s*[a-z]{3}\s*$/i;

/**
 * The error name for all currency validation type errors.
 */
const VALIDATION_ERR_NAME = "CurrencyValidationError";

/**
 * Type error to indicate that either currency has not been provided,
 * or the provided currency could not be normalized to a currency-like
 * three-letter ISO 4217 code.
 */
const ERR_EMPTY_CURRENCY = __createCurrencyValidationError__(
  "ERR_EMPTY_CURRENCY"
);

/**
 * Type error to indicate that the provided currency (after it has been
 * normalized) does not conform with the three-letter ISO 4217 currency
 * code format (https://en.wikipedia.org/wiki/ISO_4217).
 */
const ERR_ABNORMAL_CURRENCY = __createCurrencyValidationError__(
  "ERR_ABNORMAL_CURRENCY"
);

/**
 * Type error to indicate that the provided currency (after it has been
 * normalized) either does not exist as a currency on the CURRENCIES map,
 * or does not reflect the presence of the FLAG_SUPPORTED attribute in
 * its attribute flags.
 */
const ERR_UNSUPPORTED_CURRENCY = __createCurrencyValidationError__(
  "ERR_UNSUPPORTED_CURRENCY"
);

/**
 * Produces a currency-like string for the specified currency value. If the
 * currency value is currency-like, it normalizes the value and returns the
 * normalized string. Otherwise, it returns the `DEFAULT_NO_CURRENCY` value.
 *
 * @param {*} currency The currency value.
 * @return {string} The currency-like string.
 */
const __currency__ = (function () {
  function Currency(currency) {
    const currencyLike = __isCurrencyLike__(currency);
    const currencyNormalized = __normalize__(currency);

    const currencyString = currencyLike
      ? currencyNormalized
      : DEFAULT_NO_CURRENCY;

    const currencyValue = CURRENCIES[currencyString] || 0;

    Object.defineProperties(this, {
      isEmpty: {
        value: constant(!currencyNormalized)
      },

      isCurrencyLike: {
        value: constant(currencyLike)
      },

      toString: {
        value: constant(currencyString)
      },

      valueOf: {
        value: constant(currencyValue)
      }
    });
  }

  Object.defineProperties(Currency.prototype, {
    test: {
      value: function (flag) {
        if (this.isCurrencyLike()) {
          flag = Number.isInteger(flag)
            ? // Allow only positive integers.
            // Discard unnecessary bits (by &-ing with FLAG_ALL).
            Math.max(flag, 0) & FLAG_ALL
            : 0;

          return (this & flag) === flag;
        }

        return false;
      }
    }
  });

  return function __currency__(currency) {
    return new Currency(currency);
  };
})();

/**
 * Setup the `Currency` object and define the desired exposed properties
 * on it. `__parseAllCurrenciesOnce__()` is called here to create the
 * currencies lists before creating the object. This is achieved by
 * wrapping the logic inside an IIFE (immediately invoked function
 * expression) that returns the created `Currency` object.
 *
 * The `Currency` object is frozen to prevent undesired extensions. It
 * will be used as the default export for this module.
 */
const Currency = (function () {
  const { supported, rounded, dcc } = __parseAllCurrenciesOnce__();

  return Object.freeze(
    withProperties(Object.create(null), {
      /**
       * List of DCC (Dynamic Currency Conversion) target currencies.
       * @type {array}
       * @see __parseAllCurrenciesOnce__
       */
      DCC: dcc,

      /**
       * List of currencies that require rounded amounts.
       * @type {array}
       * @see __parseAllCurrenciesOnce__
       */
      ROUNDED: rounded,

      /**
       * List of supported currencies.
       * @type {array}
       * @see __parseAllCurrenciesOnce__
       */
      SUPPORTED: supported,

      /**
       * Default currency-like string that can be used in the absence
       * of a currency.
       * @type {string}
       */
      NONE: DEFAULT_NO_CURRENCY,

      /**
       * Error name for all currency validation type errors.
       * @type {string}
       */
      VALIDATION_ERR: VALIDATION_ERR_NAME,

      /**
       * Flag for DCC (Dynamic Currency Conversion) target currencies.
       * @type {integer}
       */
      FLAG_DCC: FLAG_DCC_TARGET,

      /**
       * Flag for currencies that require rounded amounts.
       * @type {integer}
       */
      FLAG_ROUNDED: FLAG_ROUNDED_AMOUNT,

      /**
       * Flag for supported currencies.
       * @type {integer}
       */
      FLAG_SUPPORTED: FLAG_SUPPORTED,

      /**
       * Produce a currency-like string from the specified curreny value.
       * @type {function}
       * @see __currency__
       */
      from: __currency__,

      /**
       * Validate a currency value against specified constraints.
       * @type {function}
       * @see __validate__
       */
      validate: __validate__,

      /**
       * Validate a currency value against specified constraints. Default
       * error handling to provide descriptive validation error messages.
       * @type {function}
       * @see __defaultValidate__
       */
      defaultValidate: __defaultValidate__,

      /**
       * Check if the specified currency value can be normalized into a
       * currency-like string.
       * @type {function}
       * @see __isCurrencyLike__
       */
      isCurrencyLike: __isCurrencyLike__,

      /**
       * Check if the specified currency can be found in the list of
       * supported currencies.
       * @type {function}
       * @see __isSupported__
       */
      isSupported: __isSupported__,

      /**
       * Check if the specified currency is supported for payments and also
       * a DCC target currency.
       * @type {function}
       * @see __isDccTarget__
       */
      isDCCTarget: __isDccTarget__,

      /**
       * Check if the specified currency is supported for payments and also
       * requires that amounts are always rounded.
       * @type {function}
       * @see __usesRoundedAmount__
       */
      usesRoundedAmount: __usesRoundedAmount__
    })
  );
})();

/**
 * Creates and returns a freezed currency validation TypeError object with
 * the specified label as message. The name property of the error object is
 * set to `VALIDATION_ERR_NAME`. The error label is normalized to screaming
 * snake case before it can be used as message for the error object.
 *
 * @param {*} label The error label.
 * @return {TypeError} A freezed currency validation TypeError object.
 */
function __createCurrencyValidationError__(label) {
  // Normalize label to screaming snake case before creating TypeError
  const err = new TypeError(snakeCase(label).toUpperCase() || undefined);
  err.name = VALIDATION_ERR_NAME;
  return Object.freeze(err);
}

/**
 * Parses the CURRENCIES map and creates lists that group currencies
 * according to their attributes. Here are the lists of currencies:
 *
 *   - supported
 *     List of all currencies with the FLAG_SUPPORTED attribute. That is,
 *     all currencies that are currently supported for payments.
 *
 *   - rounded
 *     List of all currencies with the FLAG_ROUNDED_AMOUNT attribute. That
 *     is, all currencies that require their amounts to always be rounded.
 *
 *   - dcc
 *     List of all currencies with the FLAG_DCC_TARGET attribute. That is,
 *     all currencies that can be converted to via the available Dynamic
 *     Currency Conversion(DCC) mechanisms.
 *
 * The parsing and lists creation happens only on the first function call.
 * When the currency lists have been created, they are cached (via a
 * closure) so that subsequent calls to this function will simply return
 * the lists without any extra computation.
 *
 * To achieve the one-time computation and result caching, this function
 * is overriden (only on the first call) with another function that is
 * guaranteed to always return the resulting value of the first computation
 * (via the `constant()` helper).
 *
 * @return {object} An object with three properties: supported, rounded,
 *                  dcc — each containing the corresponding lists of
 *                  currencies.
 */
function __parseAllCurrenciesOnce__() {
  const supported = [];
  const rounded = [];
  const dcc = [];

  Object.keys(CURRENCIES).forEach(function parseCurrency_(currency) {
    const currencyFlag = CURRENCIES[(currency = __normalize__(currency))] || 0;

    if (currencyFlag) {
      // Check if the FLAG_SUPPORTED bit is turned on for the currency.
      // If it is, add the currency to the `supported` list.
      currencyFlag & FLAG_SUPPORTED && supported.push(currency);

      // Check if the FLAG_DCC_TARGET bit is turned on for the currency.
      // If it is, add the currency to the `dcc` list.
      currencyFlag & FLAG_DCC_TARGET && dcc.push(currency);

      // Check if the FLAG_ROUNDED_AMOUNT bit is turned on for the currency.
      // If it is, add the currency to the `rounded` list.
      currencyFlag & FLAG_ROUNDED_AMOUNT && rounded.push(currency);
    }
  });

  /**
   * Aggregate the lists of currencies into an object with corresponding
   * keys.
   *
   * Override the original function with a function that is guaranteed to
   * always return the aggregated lists object on every call (using the
   * `constant()` helper).
   */
  __parseAllCurrenciesOnce__ = constant({
    supported: Object.freeze(supported),
    rounded: Object.freeze(rounded),
    dcc: Object.freeze(dcc)
  });

  // Call the overriden (constant) function and return the result (the
  // aggregated lists object).
  return __parseAllCurrenciesOnce__();
}

/**
 * Attempts to normalize the given currency value. If the passed value is a
 * `string`, the normalization involves trimming the leading and trailing
 * whitespaces from the value, and changing its case to uppercase.
 *
 * @param {*} currency The value to be normalized.
 * @return {string} The normalized string if `currency` is a `string`, and
 *                  an empty string otherwise.
 */
function __normalize__(currency) {
  return isString(currency) ? currency.trim().toUpperCase() : "";
}

/**
 * Checks if the given currency value will produce a currency-like string
 * (that is, a string that conforms to the three-letter ISO 4217 currency
 * code format) when it is normalized.
 *
 * @param {*} currency The currency value to check.
 * @return {boolean} True if `currency` will be currency-like when normalized,
 *                   and false otherwise.
 */
function __isCurrencyLike__(currency) {
  return REGEX_LIKE_CURRENCY.test(String(currency));
}

/**
 * Checks if the given currency value (after it has been normalized), exists
 * on the `supported` currencies list. If the value is not currency-like, it
 * skips the list check and returns false.
 *
 * @param {*} currency The currency value.
 * @return {boolean} True if it exists, and false otherwise.
 *
 * @see __parseAllCurrenciesOnce__
 */
function __isSupported__(currency) {
  return __currency__(currency).test(FLAG_SUPPORTED);
}

/**
 * Checks if the given currency value (after it has been normalized), exists
 * on the `dcc` currencies list. If the value is not currency-like, it skips
 * the list check and returns false.
 *
 * @param {*} currency The currency value.
 * @return {boolean} True if it exists, and false otherwise.
 *
 * @see __parseAllCurrenciesOnce__
 */
function __isDccTarget__(currency, supported) {
  return __currency__(currency).test(
    FLAG_DCC_TARGET | (supported === true && FLAG_SUPPORTED)
  );
}

/**
 * Checks if the given currency value (after it has been normalized), exists
 * on the `rounded` currencies list. If the value is not currency-like, it
 * skips the list check and returns false.
 *
 * @param {*} currency The currency value.
 * @return {boolean} True if it exists, and false otherwise.
 *
 * @see __parseAllCurrenciesOnce__
 */
function __usesRoundedAmount__(currency, supported) {
  return __currency__(currency).test(
    FLAG_ROUNDED_AMOUNT | (supported === true && FLAG_SUPPORTED)
  );
}

/**
 * Validates the specified currency value based on the defined constraints
 * and returns `true` if the currency value is valid. If not valid, it throws
 * an error (default behavior — if validation errors are not silenced),
 * otherwise, it returns `false` (if validation errors are silent).
 *
 * The currency can be specified as a currency value, or as a validation object
 * containing the following fields:
 *
 *   - currency
 *     The currency value to validate
 *
 *   - support
 *     A boolean indicating that validation should also assert currency support
 *     in addition to the currency value being currency-like. Unless it is set
 *     to `false`, the `ERR_UNSUPPORTED_CURRENCY` error will be thrown for
 *     currency values do not pass the `__isSupported__()` check.
 *
 *   - silent
 *     A boolean indicating that validation errors should be silenced (not be
 *     thrown). If a boolean value is passed as second argument (`silent`) to
 *     the `__validate__()` function, that value is used instead of the value
 *     set here.
 *
 * If a boolean value is passed as second argument (`silent`) passed to this
 * function, it overrides whatever value was set in the `silent` property of the
 * `currency` validation object (if applicable).
 *
 * By default, validation errors are thrown. Unless error silencing is activated
 * explicitly by setting the final value of the `silent` config to `true`, in
 * which case the `__validate__()` function returns `false` instead.
 *
 * @param {*} currency The currency value or validation object.
 * @param {*} silent Supposed boolean to override validation error silencing.
 * @return {boolean} True if valid, false only if validation errors are silent.
 * @throws {TypeError|Error}
 */
function __validate__(currency, silent) {
  const _silent = silent;
  let support;

  ({ currency, support, silent } = isPlainObject(currency)
    ? currency
    : { currency });

  currency = __currency__(currency);

  // Resolve the `silent` value to a boolean.
  // Prefer argument to property on the validation object, only if it's boolean.
  silent = isBoolean(_silent) ? _silent : silent === true;

  support = support !== false && (support & FLAG_ALL || FLAG_SUPPORTED);

  try {
    // Throw ERR_EMPTY_CURRENCY if currency value cannot be normalized or if it
    // normalizes to an empty string.
    if (currency.isEmpty()) throw ERR_EMPTY_CURRENCY;

    // Throw ERR_ABNORMAL_CURRENCY if currency value is not currency-like.
    if (!currency.isCurrencyLike()) throw ERR_ABNORMAL_CURRENCY;

    // Unless `support` field is set to `false`, check if currency value exists
    // in the `supported` currencies list, and throw ERR_UNSUPPORTED_CURRENCY if
    // it does not.
    if (support && !currency.test(support)) {
      throw ERR_UNSUPPORTED_CURRENCY;
    }

    // Return true if currency is valid, given all the available constraints.
    return silent ? true : String.call(null, currency);
  } catch (e) {
    // Return false instead of throwing the error, if the `silent` field is set
    // to `true` (explicitly).
    if (silent) return false;

    throw e;
  }
}

/**
 * Same as the `__validate__()` function but with default error handling for
 * known currency validation errors. The default error handling basically
 * rethrows new validation errors with error messages that are more
 * descriptive than the original validation errors.
 *
 * @param {*} currency The currency value or validation object.
 * @param {*} silent Supposed boolean to override validation error silencing.
 * @return {boolean} True if valid, false only if validation errors are silent.
 * @throws {TypeError|Error}
 *
 * @see __validate__
 */
function __defaultValidate__(currency, silent) {
  try {
    return __validate__(currency, silent);
  } catch (e) {
    // Override the error with descriptive message string for known currency
    // validation errors.
    switch (e) {
      case ERR_EMPTY_CURRENCY: {
        e = "Currency not provided or could not be resolved.";
        break;
      }

      case ERR_ABNORMAL_CURRENCY: {
        e = "Currency not provided in three-letter code format.";
        break;
      }

      case ERR_UNSUPPORTED_CURRENCY: {
        e = "Currency not supported for payments at this time.";
        break;
      }
    }

    // If error is a string, it means it has been overriden with a message
    // earlier. Throw a new TypeError with the descriptive message. Also,
    // set the name property of the error object to `VALIDATION_ERR_NAME`.
    if (isString(e)) {
      const err = new TypeError(e);
      err.name = VALIDATION_ERR_NAME;
      throw err;
    }

    // Otherwise, throw a fallback error.
    throw new Error("Currency validation failed.");
  }
}

/**
 * Export the `Currency`object with it's exposed properties as the
 * default export for this module.
 */
export default Currency;
